import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageHeader } from '../components/Layout';
import { Ic } from '../design/icons';
import { Avatar, Chip } from '../design/primitives';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Workspace üyesi rolü WorkspaceMember.role kolonunda workspace başına saklanır.
// Aynı kullanıcı bir workspace'te takım_lideri, başka workspace'te
// proje_yoneticisi olabilir. Sahip rolü workspace başına bir tek kişide
// bulunabilir, davet edilemez.
const ROLE_ORDER = ['admin', 'proje_yoneticisi', 'takim_lideri', 'takim_uyesi'];
const ROLE_LABELS = {
  owner: 'Sahip',
  admin: 'Admin',
  proje_yoneticisi: 'Proje Yöneticisi',
  takim_lideri: 'Takım Lideri',
  takim_uyesi: 'Takım Üyesi',
};
const ROLE_COLOR = {
  owner: '#b45309',
  admin: 'var(--danger)',
  proje_yoneticisi: '#6c4ca9',
  takim_lideri: 'var(--accent)',
  takim_uyesi: 'var(--good)',
};

// Davet rolü ve workspace rolü artık aynı vokabülere sahip; davet sırasında
// owner atanamaz.
const WORKSPACE_INVITE_ROLES = {
  admin: 'Admin — workspace yönetimi (üye, billing dahil)',
  proje_yoneticisi: 'Proje Yöneticisi — proje ve görev yönetimi',
  takim_lideri: 'Takım Lideri — atama ve revizyon yetkili',
  takim_uyesi: 'Takım Üyesi — temel görev katılımı',
};

const AVAILABLE_PERMISSIONS = [
  { id: 'can_create_tasks', label: 'Görev Oluşturabilir', description: 'Yeni görev oluşturabilir', group: 'Görev' },
  { id: 'can_view_all_tasks', label: 'Tüm Görevleri Görüntüleyebilir', description: 'Atanmamış görevleri de görebilir', group: 'Görev' },
  { id: 'can_edit_tasks', label: 'Görevleri Düzenleyebilir', description: 'Görev bilgilerini değiştirebilir', group: 'Görev' },
  { id: 'can_delete_tasks', label: 'Görevleri Silebilir', description: 'Görevleri kalıcı olarak silebilir', group: 'Görev' },
  { id: 'can_move_tasks', label: 'Görevleri Taşıyabilir', description: 'Kanban kolonlar arası taşıma', group: 'Görev' },
  { id: 'can_assign_tasks', label: 'Görev Atayabilir', description: 'Görevleri kullanıcılara atayabilir', group: 'Görev' },
  { id: 'can_complete_tasks', label: 'Görevleri Tamamlayabilir', description: 'Görevleri tamamlayabilir', group: 'Görev' },
  { id: 'can_send_revision', label: 'Revizyona Gönderebilir', description: 'Görevleri revize edebilir', group: 'Revizyon' },
  { id: 'can_resolve_revision', label: 'Revizyonu Tamamlayabilir', description: 'Revizyon sürecini tamamlar', group: 'Revizyon' },
  { id: 'can_comment', label: 'Yorum Yapabilir', description: 'Görevlere yorum ekleyebilir', group: 'İletişim' },
  { id: 'can_upload_files', label: 'Dosya Yükleyebilir', description: 'Görevlere dosya ekleyebilir', group: 'İletişim' },
  { id: 'can_download_files', label: 'Dosya İndirebilir', description: 'Görev dosyalarını indirebilir', group: 'İletişim' },
  { id: 'can_delete_files', label: 'Dosya Silebilir', description: 'Görev dosyalarını silebilir', group: 'İletişim' },
  { id: 'can_manage_users', label: 'Kullanıcıları Yönetebilir', description: 'Kullanıcı ekle/düzenle/sil', group: 'Yönetim' },
  { id: 'can_manage_roles', label: 'Rolleri Yönetebilir', description: 'Rol tanımlamalarını düzenler', group: 'Yönetim' },
  { id: 'can_manage_projects', label: 'Projeleri Yönetebilir', description: 'Proje oluştur/düzenle/sil', group: 'Yönetim' },
  { id: 'can_manage_teams', label: 'Takımları Yönetebilir', description: 'Takım oluştur/düzenle/sil', group: 'Yönetim' },
  { id: 'can_manage_workflows', label: 'İş Akışlarını Yönetebilir', description: 'Workflow tanımlamalarını düzenler', group: 'Yönetim' },
  { id: 'can_manage_tags', label: 'Etiketleri Yönetebilir', description: 'Tag/etiket oluştur/düzenle', group: 'Yönetim' },
  { id: 'can_approve_requests', label: 'Onay İsteklerini Yönetebilir', description: 'Onay isteklerini onay/red', group: 'Onay' },
  { id: 'can_send_for_review', label: 'Kontrol İçin Gönderebilir', description: 'Görevleri kontrole gönderir', group: 'Onay' },
  { id: 'can_view_reports', label: 'Raporları Görüntüleyebilir', description: 'Rapor sayfasına erişir', group: 'Raporlama' },
  { id: 'can_view_dashboard', label: 'Dashboard Görüntüleyebilir', description: 'Dashboard sayfasına erişir', group: 'Raporlama' },
];

function expiresInLabel(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'süresi doldu';
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))} dk`;
  if (h < 24) return `${h} sa`;
  return `${Math.floor(h / 24)} gün`;
}

export default function WorkspaceMembers() {
  const { user } = useContext(AuthContext);
  const { activeWorkspaceId, activeWorkspace, isWorkspaceAdmin, isSuperAdmin } = useWorkspace();
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [usage, setUsage] = useState(null);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'takim_uyesi' });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  const [showAssign, setShowAssign] = useState(false);
  const [memberToAssign, setMemberToAssign] = useState(null);
  const [assignDraft, setAssignDraft] = useState({ teamIds: [], projectIds: [] });

  const [showPermissions, setShowPermissions] = useState(false);
  const [memberToEditPerms, setMemberToEditPerms] = useState(null);
  // Modal state'inde tüm seçili permissions'ı tutuyoruz; save sırasında
  // role'den gelenleri çıkarıp sadece extra'ları persist ediyoruz.
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [rolePermsMap, setRolePermsMap] = useState({});  // { roleName: [perm,...] }

  const [showRemove, setShowRemove] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);

  const [showRevoke, setShowRevoke] = useState(false);
  const [inviteToRevoke, setInviteToRevoke] = useState(null);

  const canManage = isWorkspaceAdmin || isSuperAdmin;

  useEffect(() => {
    if (!activeWorkspaceId) return;
    Promise.all([fetchMembers(), fetchProjects(), fetchTeams(), fetchAllUsers(), fetchRolePerms()])
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId]);

  const fetchMembers = async () => {
    if (!activeWorkspaceId) return;
    try {
      const res = await axios.get(`${API}/workspaces/${activeWorkspaceId}/members`);
      setMembers(res.data?.members || []);
      setInvites(res.data?.invites || []);
      setUsage(res.data?.usage || null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Üyeler yüklenemedi';
      toast.error(msg);
    }
  };
  const fetchProjects = async () => { try { const r = await axios.get(`${API}/projects`); setProjects(r.data); } catch {} };
  const fetchTeams = async () => { try { const r = await axios.get(`${API}/teams`); setTeams(r.data); } catch {} };
  const fetchAllUsers = async () => { try { const r = await axios.get(`${API}/users`); setAllUsers(r.data); } catch {} };
  const fetchRolePerms = async () => {
    try {
      const r = await axios.get(`${API}/roles`);
      const map = {};
      for (const role of r.data || []) map[role.name] = role.permissions || [];
      setRolePermsMap(map);
    } catch {}
  };

  const userExtras = useMemo(() => {
    const map = {};
    for (const u of allUsers) {
      map[u.id] = { teamIds: u.teamIds || [], projectIds: u.projectIds || [], permissions: u.permissions || [] };
    }
    return map;
  }, [allUsers]);

  const resetInviteForm = () => setInviteData({ email: '', role: 'takim_uyesi' });

  const handleInvite = async (e) => {
    e.preventDefault();
    if (inviteSubmitting) return;
    if (!activeWorkspaceId) {
      toast.error('Aktif workspace bulunamadı');
      return;
    }
    setInviteSubmitting(true);
    try {
      await axios.post(`${API}/invites`, {
        workspaceId: activeWorkspaceId,
        email: inviteData.email.trim(),
        role: inviteData.role,
      });
      toast.success(`${inviteData.email} adresine davet gönderildi`);
      setShowInvite(false); resetInviteForm();
      fetchMembers();
    } catch (error) {
      if (error.response?.status === 402 && error.response?.data?.detail?.error === 'quota_exceeded') return;
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Hata');
      toast.error('Davet gönderilemedi: ' + message);
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    // Owner rolü değiştirilemez (workspace sahibi). Endpoint de bunu zorluyor;
    // UI tarafında defansif kontrol.
    if (member.role === 'owner') {
      toast.error('Workspace sahibinin rolü değiştirilemez');
      return;
    }
    try {
      await axios.patch(
        `${API}/workspaces/${activeWorkspaceId}/members/${member.userId}`,
        { role: newRole }
      );
      toast.success('Rol güncellendi');
      fetchMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Rol güncellenemedi';
      toast.error(msg);
    }
  };

  const openAssign = (m) => {
    const extras = userExtras[m.userId] || { teamIds: [], projectIds: [] };
    setMemberToAssign(m);
    setAssignDraft({ teamIds: [...extras.teamIds], projectIds: [...extras.projectIds] });
    setShowAssign(true);
  };

  const handleAssignSave = async () => {
    if (!memberToAssign) return;
    try {
      await axios.patch(`${API}/users/${memberToAssign.userId}`, {
        teamIds: assignDraft.teamIds,
        projectIds: assignDraft.projectIds,
      });
      toast.success('Takım/proje atamaları güncellendi');
      setShowAssign(false); setMemberToAssign(null);
      fetchAllUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Atama güncellenemedi');
    }
  };

  const openPerms = (m) => {
    // Modal state: rol'den gelen yetkiler + bu üyeye özel ekstra'lar.
    // Save sırasında rol'den geleni çıkarıp sadece ekstra'ları persist edeceğiz.
    const rolePerms = rolePermsMap[m.role] || [];
    const extras = m.extraPermissions || [];
    setMemberToEditPerms(m);
    setSelectedPermissions(Array.from(new Set([...rolePerms, ...extras])));
    setShowPermissions(true);
  };

  const togglePerm = (id) => {
    if (!memberToEditPerms) return;
    // Rol'den gelen yetkiler kilitli — bu modal'dan kaldırılamaz.
    const rolePerms = rolePermsMap[memberToEditPerms.role] || [];
    if (rolePerms.includes(id) || rolePerms.includes('all')) return;
    setSelectedPermissions(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const handleUpdatePermissions = async () => {
    if (!memberToEditPerms) return;
    const rolePerms = new Set(rolePermsMap[memberToEditPerms.role] || []);
    // Sadece rol'den gelmeyen seçili yetkileri "extraPermissions" olarak gönder.
    const extras = selectedPermissions.filter(p => !rolePerms.has(p));
    try {
      await axios.patch(
        `${API}/workspaces/${activeWorkspaceId}/members/${memberToEditPerms.userId}`,
        { extraPermissions: extras }
      );
      toast.success('Yetkiler güncellendi');
      setShowPermissions(false); setMemberToEditPerms(null);
      fetchMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Yetki güncellemesi başarısız');
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    try {
      await axios.delete(`${API}/workspaces/${activeWorkspaceId}/members/${memberToRemove.userId}`);
      toast.success('Üye workspace\'ten çıkarıldı');
      setShowRemove(false); setMemberToRemove(null);
      fetchMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Çıkarma başarısız');
    }
  };

  const handleResendInvite = async (invite) => {
    try {
      await axios.post(`${API}/invites/${invite.id}/resend`);
      toast.success('Davet maili tekrar gönderildi');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Tekrar gönderilemedi');
    }
  };

  const handleRevokeInvite = async () => {
    if (!inviteToRevoke) return;
    try {
      await axios.delete(`${API}/invites/${inviteToRevoke.id}`);
      toast.success('Davet iptal edildi');
      setShowRevoke(false); setInviteToRevoke(null);
      fetchMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'İptal başarısız');
    }
  };

  // m.role artık WorkspaceMember.role (workspace başına saklanan, owner +
  // admin/proje_yoneticisi/takim_lideri/takim_uyesi vokabülerinde).
  // User.status (suspended) ise globaldir, "Aktif/Pasif" çipi onu yansıtır.
  const userStatusById = useMemo(() => {
    const map = {};
    for (const u of allUsers) map[u.id] = u.status || 'active';
    return map;
  }, [allUsers]);

  const filteredMembers = useMemo(() => members.filter(m => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
    const matchRole = filterRole === 'all' || m.role === filterRole;
    return matchSearch && matchRole;
  }), [members, searchTerm, filterRole]);

  const stats = useMemo(() => {
    const byRole = { owner: 0, admin: 0, proje_yoneticisi: 0, takim_lideri: 0, takim_uyesi: 0 };
    let softLocked = 0;
    let suspendedCount = 0;
    members.forEach(m => {
      if (m.role && byRole[m.role] !== undefined) byRole[m.role]++;
      if (m.status === 'soft_locked') softLocked++;
      if (userStatusById[m.userId] === 'suspended') suspendedCount++;
    });
    return { byRole, softLocked, suspended: suspendedCount, total: members.length, pending: invites.length };
  }, [members, invites, userStatusById]);

  const teamsInWorkspace = useMemo(
    () => teams.filter(t => !activeWorkspaceId || !t.workspaceId || t.workspaceId === activeWorkspaceId),
    [teams, activeWorkspaceId]
  );
  const projectsInWorkspace = useMemo(
    () => projects.filter(p => !activeWorkspaceId || !p.workspaceId || p.workspaceId === activeWorkspaceId),
    [projects, activeWorkspaceId]
  );

  const teamCountByUser = useMemo(() => {
    const counts = {};
    for (const m of members) {
      const extras = userExtras[m.userId];
      if (!extras) { counts[m.userId] = 0; continue; }
      counts[m.userId] = teamsInWorkspace.filter(t => extras.teamIds?.includes(t.id)).length;
    }
    return counts;
  }, [members, userExtras, teamsInWorkspace]);

  const projectCountByUser = useMemo(() => {
    const counts = {};
    for (const m of members) {
      const extras = userExtras[m.userId];
      if (!extras) { counts[m.userId] = 0; continue; }
      counts[m.userId] = projectsInWorkspace.filter(p => extras.projectIds?.includes(p.id)).length;
    }
    return counts;
  }, [members, userExtras, projectsInWorkspace]);

  const actions = (
    <div className="flex items-center gap-2">
      {canManage && (
        <>
          <button className="ox-btn" onClick={() => navigate('/roles')}>
            {Ic.shield} Roller
          </button>
          <button className="ox-btn ox-btn-primary" onClick={() => { resetInviteForm(); setShowInvite(true); }}>
            {Ic.plus} Davet Gönder
          </button>
        </>
      )}
    </div>
  );

  usePageHeader([{ label: 'Üyeler' }], actions, [canManage]);

  return (
    <div className="ox-page space-y-4">
      {/* Stats */}
      <div className="ox-grid-stats-6">
        <div className="ox-surface rounded-md p-3">
          <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">Üye</div>
          <div className="ox-tnum text-[22px] font-semibold mt-1">{stats.total}</div>
          {usage && !usage.seats?.unlimited && (
            <div className="text-[10.5px] ox-text-dim mt-0.5">/ {usage.seats?.limit} koltuk</div>
          )}
        </div>
        <div className="ox-surface rounded-md p-3">
          <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">Bekleyen Davet</div>
          <div className="ox-tnum text-[22px] font-semibold mt-1" style={{ color: 'var(--accent)' }}>{stats.pending}</div>
        </div>
        <div className="ox-surface rounded-md p-3">
          <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">Admin</div>
          <div className="ox-tnum text-[22px] font-semibold mt-1" style={{ color: 'var(--danger)' }}>{stats.byRole.admin || 0}</div>
        </div>
        <div className="ox-surface rounded-md p-3">
          <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">Yöneticiler</div>
          <div className="ox-tnum text-[22px] font-semibold mt-1" style={{ color: '#6c4ca9' }}>{stats.byRole.proje_yoneticisi || 0}</div>
        </div>
        <div className="ox-surface rounded-md p-3">
          <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">Üye / Lider</div>
          <div className="ox-tnum text-[22px] font-semibold mt-1" style={{ color: 'var(--accent)' }}>{(stats.byRole.takim_lideri || 0) + (stats.byRole.takim_uyesi || 0)}</div>
        </div>
        <div className="ox-surface rounded-md p-3">
          <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">Pasif</div>
          <div className="ox-tnum text-[22px] font-semibold mt-1" style={{ color: 'var(--warning)' }}>{stats.suspended}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="ox-surface rounded-md p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 sm:min-w-[240px] sm:flex-none basis-full sm:basis-auto">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 ox-text-dim">{Ic.search}</span>
          <input
            className="ox-input pl-8"
            placeholder="İsim veya email ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select className="ox-select" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="all">Tüm Roller</option>
          {ROLE_ORDER.map(k => <option key={k} value={k}>{ROLE_LABELS[k]}</option>)}
        </select>
        {(searchTerm || filterRole !== 'all') && (
          <button className="ox-btn ox-btn-ghost" onClick={() => { setSearchTerm(''); setFilterRole('all'); }}>
            {Ic.x} Temizle
          </button>
        )}
      </div>

      {/* Members Table */}
      <div className="ox-surface rounded-md overflow-hidden">
        <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="text-[12.5px] font-semibold">Workspace Üyeleri{activeWorkspace?.name ? ` — ${activeWorkspace.name}` : ''}</div>
        </div>
        {loading ? (
          <div className="p-12 text-center ox-text-muted">Yükleniyor...</div>
        ) : filteredMembers.length === 0 ? (
          <div className="p-12 text-center">
            <div className="ox-text-dim inline-flex mb-3">{Ic.users}</div>
            <div className="text-[13px] ox-text-muted">Üye bulunamadı</div>
          </div>
        ) : (
          <div className="ox-table-wrap ox-table-wide">
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-muted)' }}>
                <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Kullanıcı</th>
                <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Rol</th>
                <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Aktif</th>
                <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Takımlar</th>
                <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Projeler</th>
                <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden lg:table-cell">Katıldı</th>
                <th className="text-right px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((m) => {
                const isSelf = m.userId === user?.id;
                const isOwner = m.role === 'owner';
                const editable = canManage && !isOwner && !isSelf;
                const teamCount = teamCountByUser[m.userId] || 0;
                const projectCount = projectCountByUser[m.userId] || 0;
                const wsRole = m.role || 'takim_uyesi';
                const globalStatus = userStatusById[m.userId] || 'active';
                const isSuspended = globalStatus === 'suspended';
                return (
                  <tr key={m.id || m.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar u={m} imgSrc={m.picture} size={28} />
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium truncate">
                            {m.name || m.email || '(isimsiz)'}{isSelf && <span className="ox-text-muted"> (Siz)</span>}
                          </div>
                          <div className="text-[11px] ox-text-muted truncate">{m.email}</div>
                        </div>
                        {m.status === 'soft_locked' && (
                          <Chip color="var(--warning)" bg="var(--warning-weak)">Soft-locked</Chip>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {editable ? (
                        <select
                          className="ox-select"
                          style={{ minWidth: 150, padding: '4px 8px', fontSize: 12 }}
                          value={wsRole}
                          onChange={(e) => handleRoleChange(m, e.target.value)}
                        >
                          {ROLE_ORDER.map(k => <option key={k} value={k}>{ROLE_LABELS[k]}</option>)}
                        </select>
                      ) : (
                        <Chip color={ROLE_COLOR[wsRole] || 'var(--text-muted)'} bg="var(--surface-muted)">
                          {ROLE_LABELS[wsRole] || wsRole}
                        </Chip>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {isSuspended ? (
                        <Chip color="var(--warning)" bg="var(--warning-weak)">Pasif</Chip>
                      ) : (
                        <Chip color="var(--good)" bg="var(--good-weak)">Aktif</Chip>
                      )}
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell">
                      <span className="text-[12.5px]">{teamCount}</span>
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell">
                      <span className="text-[12.5px]">{projectCount}</span>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      <span className="text-[12px] ox-text-muted">
                        {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString('tr-TR') : '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-0.5 justify-end">
                        {canManage && !isOwner && (
                          <>
                            <button className="ox-btn ox-btn-ghost p-1.5" title="Takım & Proje Atamaları"
                              onClick={() => openAssign(m)}>{Ic.edit}</button>
                            <button className="ox-btn ox-btn-ghost p-1.5" title="Yetkiler"
                              onClick={() => openPerms(m)}>{Ic.shield}</button>
                            {!isSelf && (
                              <button className="ox-btn ox-btn-ghost p-1.5" title="Workspace'ten Çıkar"
                                style={{ color: 'var(--danger)' }}
                                onClick={() => { setMemberToRemove(m); setShowRemove(true); }}>
                                {Ic.userX || Ic.trash}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Pending Invites */}
      {canManage && invites.length > 0 && (
        <div className="ox-surface rounded-md overflow-hidden">
          <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="text-[12.5px] font-semibold">Bekleyen Davetler ({invites.length})</div>
          </div>
          <div className="ox-table-wrap">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-muted)' }}>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">E-posta</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Rol</th>
                  <th className="text-left px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Süre</th>
                  <th className="text-right px-3 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-3 py-2.5">
                      <div className="text-[13px]">{inv.email}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <Chip color={ROLE_COLOR[inv.role] || 'var(--text-muted)'} bg="var(--surface-muted)">
                        {ROLE_LABELS[inv.role] || inv.role}
                      </Chip>
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell">
                      <span className="text-[12px] ox-text-muted">{expiresInLabel(inv.expiresAt)}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-0.5 justify-end">
                        <button className="ox-btn ox-btn-ghost p-1.5" title="Tekrar Gönder"
                          onClick={() => handleResendInvite(inv)}>{Ic.refresh}</button>
                        <button className="ox-btn ox-btn-ghost p-1.5" title="Daveti İptal Et"
                          style={{ color: 'var(--danger)' }}
                          onClick={() => { setInviteToRevoke(inv); setShowRevoke(true); }}>{Ic.x}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="ox-modal-backdrop" onClick={() => setShowInvite(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Workspace'e Davet Gönder</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setShowInvite(false)}>{Ic.x}</button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="ox-modal-body space-y-3">
                <div className="text-[12px] ox-text-muted" style={{ lineHeight: 1.55 }}>
                  Davet gönderdiğin kişiye e-posta ile workspace'ine katılma linki iletilir.
                  Hesabı yoksa önce kayıt olur, sonra otomatik olarak{' '}
                  <strong>{activeWorkspace?.name || 'workspace'}</strong>'e katılır.
                </div>
                <div className="ox-field">
                  <label>E-posta <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input type="email" required className="ox-input" autoFocus
                    placeholder="ad.soyad@ornek.com"
                    value={inviteData.email}
                    onChange={e => setInviteData({ ...inviteData, email: e.target.value })}
                  />
                </div>
                <div className="ox-field">
                  <label>Workspace Rolü</label>
                  <select className="ox-select"
                    value={inviteData.role}
                    onChange={e => setInviteData({ ...inviteData, role: e.target.value })}>
                    {Object.entries(WORKSPACE_INVITE_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <div className="text-[11px] ox-text-muted mt-1">
                    Takım/proje atamalarını davet kabul edildikten sonra üye listesinden yapabilirsin.
                  </div>
                </div>
              </div>
              <div className="ox-modal-foot">
                <button type="button" className="ox-btn ox-btn-ghost" onClick={() => setShowInvite(false)}>İptal</button>
                <button type="submit" className="ox-btn ox-btn-primary" disabled={inviteSubmitting || !inviteData.email.trim()}>
                  {inviteSubmitting ? 'Gönderiliyor…' : 'Davet Gönder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Teams/Projects Modal */}
      {showAssign && memberToAssign && (
        <div className="ox-modal-backdrop" onClick={() => setShowAssign(false)}>
          <div className="ox-modal ox-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Takım & Proje Atamaları — {memberToAssign.name || memberToAssign.email}</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setShowAssign(false)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body space-y-3">
              <div className="ox-field">
                <label>Takımlar</label>
                <div className="text-[11px] ox-text-muted mb-1">Bu workspace'teki takımlar</div>
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 rounded" style={{ background: 'var(--surface-muted)' }}>
                  {teamsInWorkspace.length === 0 ? <span className="ox-text-muted text-[11.5px]">Henüz takım yok</span> :
                    teamsInWorkspace.map(t => {
                      const on = assignDraft.teamIds.includes(t.id);
                      return (
                        <button type="button" key={t.id} className="ox-pick" data-on={on}
                          onClick={() => setAssignDraft(d => ({
                            ...d, teamIds: on ? d.teamIds.filter(x => x !== t.id) : [...d.teamIds, t.id]
                          }))}>
                          {t.name}
                        </button>
                      );
                    })}
                </div>
              </div>
              <div className="ox-field">
                <label>Projeler</label>
                <div className="text-[11px] ox-text-muted mb-1">Erişebileceği projeler</div>
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 rounded" style={{ background: 'var(--surface-muted)' }}>
                  {projectsInWorkspace.length === 0 ? <span className="ox-text-muted text-[11.5px]">Henüz proje yok</span> :
                    projectsInWorkspace.map(p => {
                      const on = assignDraft.projectIds.includes(p.id);
                      return (
                        <button type="button" key={p.id} className="ox-pick" data-on={on}
                          onClick={() => setAssignDraft(d => ({
                            ...d, projectIds: on ? d.projectIds.filter(x => x !== p.id) : [...d.projectIds, p.id]
                          }))}>
                          {p.name}
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" onClick={() => setShowAssign(false)}>İptal</button>
              <button className="ox-btn ox-btn-primary" onClick={handleAssignSave}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissions && memberToEditPerms && (
        <div className="ox-modal-backdrop" onClick={() => setShowPermissions(false)}>
          <div className="ox-modal ox-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Yetkiler: {memberToEditPerms.name || memberToEditPerms.email}</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setShowPermissions(false)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body">
              {(() => {
                const memberRolePerms = rolePermsMap[memberToEditPerms.role] || [];
                const isFullAdmin = memberRolePerms.includes('all');
                const fromRole = (id) => memberRolePerms.includes(id) || isFullAdmin;
                const extraCount = selectedPermissions.filter(p => !memberRolePerms.includes(p)).length;
                const roleLabel = ROLE_LABELS[memberToEditPerms.role] || memberToEditPerms.role;
                return (
                  <>
                    <div className="text-[11.5px] ox-text-muted mb-3" style={{ lineHeight: 1.55 }}>
                      <strong>{roleLabel}</strong> rolü gereği işaretli yetkiler kilitli; bu modal'dan
                      sadece <strong>kullanıcıya özel ekstra yetki</strong> verilir veya kaldırılır.
                      {' '}
                      {isFullAdmin
                        ? <span style={{ color: 'var(--warning)' }}>Sahip / Admin zaten tüm yetkilere sahip — ekstra atanamaz.</span>
                        : <>Şu an <strong>{extraCount}</strong> ekstra yetki tanımlı.</>}
                    </div>
                    <div className="space-y-4">
                      {Array.from(new Set(AVAILABLE_PERMISSIONS.map(p => p.group))).map(group => (
                        <div key={group}>
                          <div className="text-[11px] uppercase tracking-wider ox-text-dim font-medium mb-1.5">{group}</div>
                          <div className="grid gap-1" style={{ gridTemplateColumns: '1fr 1fr' }}>
                            {AVAILABLE_PERMISSIONS.filter(p => p.group === group).map(p => {
                              const fromR = fromRole(p.id);
                              const checked = selectedPermissions.includes(p.id) || fromR;
                              const locked = fromR;
                              return (
                                <label key={p.id} className="flex items-start gap-2 p-2 rounded"
                                  style={{
                                    cursor: locked ? 'not-allowed' : 'pointer',
                                    opacity: locked ? 0.7 : 1,
                                    background: checked ? 'var(--accent-weak)' : 'var(--surface-muted)',
                                    border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                                  }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={locked}
                                    onChange={() => togglePerm(p.id)}
                                    className="mt-0.5"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-medium flex items-center gap-1.5">
                                      {p.label}
                                      {fromR && (
                                        <span className="text-[10px] ox-text-dim" title={`${roleLabel} rolünden geliyor`}>
                                          (rol)
                                        </span>
                                      )}
                                      {!fromR && selectedPermissions.includes(p.id) && (
                                        <span className="text-[10px]" style={{ color: 'var(--accent)' }} title="Kullanıcıya özel ekstra">
                                          (ekstra)
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[11px] ox-text-muted">{p.description}</div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" onClick={() => setShowPermissions(false)}>İptal</button>
              <button className="ox-btn ox-btn-primary" onClick={handleUpdatePermissions}>Yetkileri Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Modal */}
      {showRemove && memberToRemove && (
        <div className="ox-modal-backdrop" onClick={() => setShowRemove(false)}>
          <div className="ox-modal ox-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]" style={{ color: 'var(--danger)' }}>Workspace'ten Çıkar</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setShowRemove(false)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body">
              <p className="text-[13px] mb-2">
                <strong>{memberToRemove.name || memberToRemove.email}</strong>'i bu workspace'ten çıkarmak istediğinden emin misin?
              </p>
              <p className="text-[12px] ox-text-muted">
                Kullanıcının hesabı silinmez; sadece bu workspace'ten erişimi kalkar. Tekrar davet gönderebilirsin.
              </p>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" onClick={() => setShowRemove(false)}>İptal</button>
              <button className="ox-btn ox-btn-danger" onClick={handleRemoveMember}>Evet, Çıkar</button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Invite Modal */}
      {showRevoke && inviteToRevoke && (
        <div className="ox-modal-backdrop" onClick={() => setShowRevoke(false)}>
          <div className="ox-modal ox-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]" style={{ color: 'var(--danger)' }}>Daveti İptal Et</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setShowRevoke(false)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body">
              <p className="text-[13px]">
                <strong>{inviteToRevoke.email}</strong> için bekleyen daveti iptal etmek istediğinden emin misin?
              </p>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" onClick={() => setShowRevoke(false)}>Vazgeç</button>
              <button className="ox-btn ox-btn-danger" onClick={handleRevokeInvite}>Evet, İptal Et</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
